// ============================================================
//  SUPABASE CLIENT — Production cloud storage & database
//  File: src/lib/supabase.js
//
//  Setup: Add these public values to the frontend hosting environment:
//    VITE_SUPABASE_URL      = https://xxxx.supabase.co
//    VITE_SUPABASE_ANON_KEY = eyJhbGci...
// ============================================================

import { createClient } from "@supabase/supabase-js";
import { safeFileName } from "@/lib/adminImageUtils";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || "";
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || "";

export const missingSupabaseVariables = [
  !SUPABASE_URL && "VITE_SUPABASE_URL",
  !SUPABASE_ANON_KEY && "VITE_SUPABASE_ANON_KEY",
].filter(Boolean);

export const getSupabaseConfigurationError = () =>
  missingSupabaseVariables.map((name) => `Missing ${name}`).join(". ");

export const supabase = SUPABASE_URL && SUPABASE_ANON_KEY
  ? createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        storage: window.localStorage,
      },
    })
  : null;

export const isSupabaseReady = () => !!supabase;

// ── Table names ───────────────────────────────────────────────
export const TABLES = {
  settings:     "mff_settings",
  weddings:     "mff_weddings",
  films:        "mff_films",
  prewedding:   "mff_prewedding",
  preweddingSessions: "mff_prewedding_sessions",
  instagram:    "mff_instagram",
  testimonials: "mff_testimonials",
  inquiries:    "mff_inquiries",
  clientGalleryEvents: "mff_client_gallery_events",
  clientGalleryShareLinks: "mff_client_gallery_share_links",
};

// ── Storage bucket name ───────────────────────────────────────
export const BUCKET = "mff-media";

// ── Upload file to Supabase Storage ──────────────────────────
export async function uploadToSupabase(file, folder = "uploads") {
  if (!supabase) {
    throw new Error(getSupabaseConfigurationError() || "Supabase is not configured");
  }
  const safeName = safeFileName(file.name);
  const cleanFolder = String(folder || "uploads")
    .replace(/^\/+|\/+$/g, "")
    .replace(/[^a-zA-Z0-9/_-]+/g, "-");
  const name = `${cleanFolder}/${Date.now()}-${crypto.randomUUID?.() || Math.random().toString(36).slice(2)}-${safeName}`;
  const { data, error } = await supabase.storage.from(BUCKET).upload(name, file, {
    cacheControl: "3600",
    upsert: false,
    contentType: file.type || "image/jpeg",
  });
  if (error) throw error;
  const { data: urlData } = supabase.storage.from(BUCKET).getPublicUrl(data.path);
  return urlData.publicUrl;
}

// ── Generic DB helpers ────────────────────────────────────────
export async function dbGet(table) {
  if (!supabase) return null;
  const { data, error } = await supabase.from(table).select("*");
  if (error) throw error;
  return data;
}

export async function dbGetOne(table, key, value) {
  if (!supabase) return null;
  const { data, error } = await supabase.from(table).select("*").eq(key, value).single();
  if (error && error.code !== "PGRST116") throw error;
  return data;
}

export async function dbUpsert(table, row, conflictKey = "id") {
  if (!supabase) return null;
  const { data, error } = await supabase.from(table).upsert(row, { onConflict: conflictKey }).select().single();
  if (error) throw error;
  return data;
}

export async function dbInsert(table, row) {
  if (!supabase) return null;
  const { data, error } = await supabase.from(table).insert(row).select().single();
  if (error) throw error;
  return data;
}

export async function dbUpdate(table, row, key, value) {
  if (!supabase) return null;
  const { data, error } = await supabase.from(table).update(row).eq(key, value).select().single();
  if (error) throw error;
  return data;
}

export async function dbDelete(table, key, value) {
  if (!supabase) return null;
  const { error } = await supabase.from(table).delete().eq(key, value);
  if (error) throw error;
  return true;
}
